import { useState, type ChangeEvent } from 'react';
import { toast } from 'react-hot-toast';
import { CheckCircle2, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Modal } from './Modal';
import type { Product } from '../mockKnowledge';

interface ImportCsvModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (products: Product[]) => void;
}

type Stage = 'idle' | 'parsing' | 'ready';

/** Hardcoded column headers standing in for a real CSV/Excel parse — there is no parsing library wired up yet. */
const DETECTED_COLUMNS = ['Product Name', 'Price', 'Desc', 'Qty'];

const FAKE_PRODUCT_NAMES = [
  'Printed Cotton Saree',
  'Men’s Slim Fit Polo',
  'Kids Woolen Sweater',
  'Bamboo Cutlery Set',
  'Leather Sandals',
  'Embroidered Hijab',
];

export function ImportCsvModal({ open, onClose, onImport }: ImportCsvModalProps) {
  const [stage, setStage] = useState<Stage>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [mapping, setMapping] = useState({
    title: DETECTED_COLUMNS[0],
    price: DETECTED_COLUMNS[1],
    description: DETECTED_COLUMNS[2],
    stock: DETECTED_COLUMNS[3],
  });

  const reset = () => {
    setStage('idle');
    setFile(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setStage('parsing');
    window.setTimeout(() => setStage('ready'), 1200);
  };

  // Deterministic-but-varied row count so re-selecting a different file feels responsive to what was "parsed".
  const rowCount = file ? Math.max(3, Math.min(8, Math.round(file.size / 1024) || 4)) : 0;

  const handleImport = () => {
    const products: Product[] = Array.from({ length: rowCount }, (_, index) => ({
      id: `csv-${Date.now()}-${index}`,
      title: FAKE_PRODUCT_NAMES[index % FAKE_PRODUCT_NAMES.length],
      price: 300 + index * 175,
      description: '',
      stock: 10 + index * 3,
      imageUrl: '/mock/placeholder.jpg',
      category: 'Imported',
      tags: ['csv-import'],
      source: 'csv',
      inKnowledgeBase: true,
    }));

    onImport(products);
    toast.success(`Imported ${products.length} products`);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import CSV / Excel"
      description="Bulk-add products from a spreadsheet export."
    >
      <div className="space-y-4">
        {stage === 'idle' && (
          <div>
            <Label htmlFor="csv-file">Choose a file</Label>
            <div className="mt-1.5 flex items-center gap-2 rounded-md border border-dashed border-border p-4">
              <Upload className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <input
                  id="csv-file"
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={handleFileChange}
                  className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-white hover:file:bg-primary/90"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">Accepts .csv or .xlsx exports from your storefront or spreadsheet.</p>
              </div>
            </div>
          </div>
        )}

        {stage === 'parsing' && (
          <div className="flex items-center gap-2.5 rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Reading {file?.name}…
          </div>
        )}

        {stage === 'ready' && file && (
          <>
            <div className="flex items-center gap-2 rounded-md border border-success/20 bg-success/5 px-3 py-2 text-xs text-success">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Detected {DETECTED_COLUMNS.length} columns and {rowCount} rows in {file.name}
            </div>

            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">Map columns to product fields</p>
              <div className="space-y-2">
                {(
                  [
                    { key: 'title', label: 'Title' },
                    { key: 'price', label: 'Price' },
                    { key: 'description', label: 'Description' },
                    { key: 'stock', label: 'Stock' },
                  ] as const
                ).map((field) => (
                  <div key={field.key} className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-xs text-muted-foreground">{field.label}</span>
                    <Select
                      value={mapping[field.key]}
                      onChange={(event) => setMapping((prev) => ({ ...prev, [field.key]: event.target.value }))}
                      aria-label={`Column mapped to ${field.label}`}
                      className="h-9"
                    >
                      {DETECTED_COLUMNS.map((column) => (
                        <option key={column} value={column}>
                          {column}
                        </option>
                      ))}
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="button" onClick={handleImport}>
                Import {rowCount} products
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default ImportCsvModal;
